import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Spin } from "antd";
import { motion } from "framer-motion";
import { IconCheck, IconX } from "@tabler/icons-react";
import { confirmAccount } from "../../services/AuthenticationService";
import { useAuth } from "../../contextProviders/AuthProvider";
import { TRANSLATIONS } from "../../constants/translations";
import { translate } from "../../helpers/translationHelper";
import NotAuthenticatedLayout from "../../components/Layouts/NotAuthenticatedLayout";
import { useAppBreakpoint } from "../../hooks/useAppBreakpoint";
import { CORAL, CORAL_TINT_DARK, TEAL_SOFT_BG } from "../../theme/creamyTokens";

type ConfirmStatus = "processing" | "success" | "error";

// Coral (brand) for the success moment, same soft-pastel confetti mix as the
// rest of the creamy palette instead of the old stand-alone teal/tan set.
const CONFETTI_COLORS = [CORAL, CORAL_TINT_DARK, "#F0C99B", TEAL_SOFT_BG, "#FFFFFF"];
const CONFETTI_COUNT = 18;

/** Fixed per-mount so pieces don't jump to new spots on a re-render, only on
 * the actual confirm-success entrance. */
const useConfettiPieces = () =>
  useMemo(
    () =>
      Array.from({ length: CONFETTI_COUNT }, (_, i) => ({
        left: 4 + ((i * 53) % 92),
        size: 6 + ((i * 7) % 8),
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        delay: (i * 0.07) % 0.6,
        duration: 1.6 + ((i * 5) % 9) / 10,
        rotate: (i * 47) % 360,
        drift: ((i % 2 === 0 ? 1 : -1) * (10 + (i % 3) * 8)),
        round: i % 3 !== 0,
      })),
    []
  );

const Confirm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { isMobile } = useAppBreakpoint();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<ConfirmStatus>("processing");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const confetti = useConfettiPieces();

  useEffect(() => {
    const confirmEmail = async () => {
      const token = searchParams.get("token");
      const userId = searchParams.get("userId");

      if (!token || !userId) {
        setErrorMessage(translate(t, TRANSLATIONS.CONFIRM_ACCOUNT_INVALID_PARAMS));
        setStatus("error");
        return;
      }

      try {
        await confirmAccount(token, userId);
        setStatus("success");
      } catch (error) {
        setErrorMessage(translate(t, TRANSLATIONS.CONFIRM_ACCOUNT_ERROR_FALLBACK));
        setStatus("error");
      }
    };

    confirmEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Auto-redirect on success -- same target the CTA button below points to,
  // just fired automatically after a moment so confirming an email always
  // lands you back at login (or "/" if this browser happens to already be
  // signed in) without needing a click.
  useEffect(() => {
    if (status !== "success") return;
    const redirectTimer = setTimeout(() => {
      navigate(isAuthenticated ? "/" : "/login");
    }, 2500);
    return () => clearTimeout(redirectTimer);
  }, [status, isAuthenticated, navigate]);

  return (
    <NotAuthenticatedLayout>
      <div
        style={{
          maxWidth: 440,
          width: "100%",
          margin: "0 auto",
          padding: isMobile ? "0 8px" : "0 16px",
        }}
      >
        <div
          style={{
            position: "relative",
            overflow: "hidden",
            background: "#FFFFFF",
            borderRadius: 28,
            boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
            padding: isMobile ? "36px 22px 32px" : "48px 36px 40px",
            textAlign: "center",
          }}
        >
        <>
          {status === "success" &&
            confetti.map((piece, i) => (
              <motion.span
                key={i}
                initial={{ opacity: 0, y: -20, x: 0, rotate: piece.rotate }}
                animate={{
                  opacity: [0, 1, 1, 0],
                  y: 260,
                  x: piece.drift,
                  rotate: piece.rotate + 240,
                }}
                transition={{ duration: piece.duration, delay: piece.delay, ease: "easeIn" }}
                style={{
                  position: "absolute",
                  top: 0,
                  left: `${piece.left}%`,
                  width: piece.size,
                  height: piece.size,
                  background: piece.color,
                  borderRadius: piece.round ? "50%" : 3,
                  pointerEvents: "none",
                }}
              />
            ))}

          {status === "processing" && (
            <div style={{ padding: "24px 0" }}>
              <Spin size="large" />
              <p style={{ marginTop: 20, color: "#667085", fontSize: 15 }}>
                {translate(t, TRANSLATIONS.CONFIRM_ACCOUNT_PROCESSING)}
              </p>
            </div>
          )}

          {status === "success" && (
            <>
              <motion.div
                initial={{ scale: 0, rotate: -120, opacity: 0 }}
                animate={{ scale: 1, rotate: 0, opacity: 1 }}
                transition={{ type: "spring", stiffness: 260, damping: 18 }}
                style={{
                  width: 84,
                  height: 84,
                  margin: "0 auto",
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${CORAL}, ${CORAL_TINT_DARK})`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 16px rgba(216,90,48,0.35)",
                }}
              >
                <IconCheck size={44} color="#FFFFFF" stroke={3} />
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15, duration: 0.35 }}
                style={{ marginTop: 24, marginBottom: 0, fontSize: 24, fontWeight: 700 }}
              >
                {translate(t, TRANSLATIONS.CONFIRM_ACCOUNT_SUCCESS_TITLE)}
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25, duration: 0.35 }}
                style={{ marginTop: 8, color: "#667085", fontSize: 15, lineHeight: 1.6 }}
              >
                {translate(
                  t,
                  isAuthenticated
                    ? TRANSLATIONS.CONFIRM_ACCOUNT_SUCCESS_AUTHENTICATED
                    : TRANSLATIONS.CONFIRM_ACCOUNT_SUCCESS_UNAUTHENTICATED
                )}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35, duration: 0.35 }}
              >
                <Button
                  type="primary"
                  shape="round"
                  size="large"
                  style={{ marginTop: 28, minWidth: 200 }}
                  onClick={() => navigate(isAuthenticated ? "/" : "/login")}
                >
                  {translate(
                    t,
                    isAuthenticated ? TRANSLATIONS.CONFIRM_ACCOUNT_CTA_EXPLORE : TRANSLATIONS.CONFIRM_ACCOUNT_CTA_LOGIN
                  )}
                </Button>
              </motion.div>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.35 }}
                style={{ marginTop: 14, marginBottom: 0, color: "#9AA5AD", fontSize: 13 }}
              >
                {translate(t, TRANSLATIONS.CONFIRM_ACCOUNT_REDIRECTING)}
              </motion.p>
            </>
          )}

          {status === "error" && (
            <>
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 260, damping: 18 }}
                style={{
                  width: 84,
                  height: 84,
                  margin: "0 auto",
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #E2A28F, #EAB9A0)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 16px rgba(226,162,143,0.4)",
                }}
              >
                <IconX size={44} color="#FFFFFF" stroke={3} />
              </motion.div>

              <h2 style={{ marginTop: 24, marginBottom: 0, fontSize: 22, fontWeight: 700 }}>
                {translate(t, TRANSLATIONS.CONFIRM_ACCOUNT_ERROR_TITLE)}
              </h2>
              <p style={{ marginTop: 8, color: "#667085", fontSize: 15, lineHeight: 1.6 }}>
                {errorMessage}
              </p>

              <Button
                shape="round"
                size="large"
                style={{ marginTop: 28, minWidth: 200, borderColor: CORAL, color: CORAL, background: "transparent" }}
                onClick={() => navigate("/")}
              >
                {translate(t, TRANSLATIONS.CONFIRM_ACCOUNT_CTA_HOME)}
              </Button>
            </>
          )}
        </>
        </div>
      </div>
    </NotAuthenticatedLayout>
  );
};

export default Confirm;
